import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetTable, ITable } from "api/rpc/2024-04/facilityAdmin/tables/tables";
import "./tableServiceToolTip.scss";
import { StatusCode } from "api/protocols";
import axios, { CancelToken } from "axios";
import Spin from "components/spin/spin";
import { LocaleCurrency } from "helpers/Locale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import RightClickMenu from "components/rightClickMenu/RightClickMenu";

interface IMenuAction {
    icon: IconName;
    iconColor?: string;
    title: string;
    disabled?: boolean;
    action: () => void;
}

interface IProps {
    table: ITable;
    children?: React.ReactNode;
    delay?: number;
    x: number;
    y: number;
    width: number;
    height: number;
    onClick?: () => void;
    menuSections?: Array<Array<IMenuAction>>;
}

const TableServiceToolTip = (props: IProps) => {
    const { x, y, width, height } = props;
    const { t } = useTranslation();
    const [active, setActive] = useState(false);
    const [show, setShow] = useState(false);
    const [updatedTable, setUpdatedTable] = useState<ITable>();
    const [offsetX, setOffsetX] = useState(0);
    const tooltipRef = useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        let timeout: NodeJS.Timeout;
        if (active) {
            timeout = setTimeout(() => setShow(true), props.delay || 750);
        } else {
            setShow(false);
            setOffsetX(0);
        }
        return () => clearTimeout(timeout);
    }, [active, props.delay]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (active) {
            void getTableDetails(source.token);
        }
        return () => source.cancel();
    }, [active]);

    // After the tooltip renders, measure its position and nudge it
    // back into view if it overflows the container edges.
    useEffect(() => {
        if (!show || !tooltipRef.current) {
            return;
        }

        const tooltip = tooltipRef.current;

    
        const container =
            (tooltip.closest(".ui-floor-plan-container")) ||
            (tooltip.closest(".table-service-floor-plan")) ||
            (tooltip.closest("[data-floor-plan]"));

        const tooltipRect = tooltip.getBoundingClientRect();
        const containerRect = container
            ? container.getBoundingClientRect()
            : { left: 0, right: window.innerWidth };

        const PADDING = 8; // minimum gap from the container edge in px

        let shift = 0;
        if (tooltipRect.left < containerRect.left + PADDING) {
            // Overflowing on the LEFT — push tooltip to the right
            shift = containerRect.left + PADDING - tooltipRect.left;
        } else if (tooltipRect.right > containerRect.right - PADDING) {
            // Overflowing on the RIGHT — push tooltip to the left
            shift = containerRect.right - PADDING - tooltipRect.right;
        }

        setOffsetX(shift);
    }, [show]);

    const toggleActive = useCallback(
        (force?: boolean) => {
            const setTo = typeof force === "boolean" ? force : !active;
            if (active === setTo) {
                return;
            }
            setActive(setTo);
        },
        [active],
    );

    async function getTableDetails(cancelToken: CancelToken) {
        if (updatedTable !== undefined) {
            setUpdatedTable(undefined);
        }
        const res = await GetTable({ id: props.table?.id, carts: true }, false, cancelToken);
        if (res?.status === StatusCode.OK) {
            setUpdatedTable(res?.data[0]);
        } else {
            setUpdatedTable(null);
        }
    }

    const triggerStyle: React.CSSProperties = {
        position: "absolute",
        left: x,
        top: y,
        width: width,
        height: height,
        zIndex: 10,
        cursor: "pointer",
    };

    const triggerHandlers = {
        onMouseEnter: () => toggleActive(true),
        onMouseLeave: () => toggleActive(false),
        onClick: props.onClick,
    };

    return (
        <>
            {/* 1. Render the original table images/badges */}
            {props.children}

            {/* 2. Invisible trigger over the table for hover / click / optional right-click menu */}
            {props.menuSections ? (
                <div style={triggerStyle}>
                    <RightClickMenu fullHeightWidth sections={props.menuSections}>
                        <div
                            className="ui-table-service-tooltip-trigger"
                            style={{ width: "100%", height: "100%", cursor: "pointer" }}
                            {...triggerHandlers}
                        />
                    </RightClickMenu>
                </div>
            ) : (
                <div className="ui-table-service-tooltip-trigger" style={triggerStyle} {...triggerHandlers} />
            )}

            {/* 3. Render the tooltip bubble anchored to the trigger position.
                   offsetX nudges it back into view when the table is near an edge. */}
            {show && (
                <div
                    ref={tooltipRef}
                    className="ui-table-service-positioned-overlay"
                    style={{
                        position: "absolute",
                        left: x + width / 2,
                        top: y,
                        // offsetX is 0 when centered, positive when pushed right, negative when pushed left
                        transform: `translateX(calc(-50% + ${offsetX}px))`,
                    }}
                >
                    <div className="ui-table-service-tooltip-overlay ui-table-service-tooltip-overlay-open">
                        <div className="ui-table-service-tooltip-overlay_content">
                            <div className="ui-table-service-tooltip-products_container">
                                <div className="ui-table-service-tooltip-rates">
                                    <p className="ui-table-service-tooltip-title_text_rates mr-4">{t('Details')}</p>
                                </div>
                                <div className="ui-table-service-tooltip-products">
                                    <div className="ui-table-service-tooltip-product_titles">
                                        <p className="ui-table-service-tooltip-title_text">{t("Table")}</p>
                                        <p className="ui-table-service-tooltip-small_text">{props.table.title || props.table.short_title}</p>
                                    </div>
                                </div>

                                {props.table.user_full_name && (
                                    <div className="ui-table-service-tooltip-products">
                                        <div className="ui-table-service-tooltip-product_titles">
                                            <p className="ui-table-service-tooltip-title_text">{t("Server")}</p>
                                            <p className="ui-table-service-tooltip-small_text">{props.table.user_full_name}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="ui-table-service-tooltip-info_container">
                                <div className="ui-table-service-tooltip-rates">
                                    <p className="ui-table-service-tooltip-title_text_rates">{t("Seats")}</p>
                                </div>
                                {updatedTable === undefined ? (
                                    <div className="flex"><Spin /></div>
                                ) : (updatedTable?.carts && updatedTable?.carts.length > 0) ? (
                                    updatedTable?.carts.map((cart, index) => (
                                        <div
                                            key={cart.id}
                                            className="ui-table-service-tooltip-title_text"
                                            style={{
                                                fontSize: "0.7rem",
                                                fontWeight: "500",
                                                display: "flex",
                                                width: "100%",
                                                alignItems: "center",
                                                gap: "10px",
                                            }}
                                        >
                                            <span style={{ whiteSpace: "nowrap", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                                                <span style={{ fontWeight: 800 }}>S{index + 1}:</span>{" "}
                                                {cart.customer?.full_name || cart.description || t("Guest")}
                                            </span>
                                            <div style={{ textAlign: "left", width: "65px" }}>
                                                <LocaleCurrency amount={cart.total_price_due} currency={cart.currency} />
                                            </div>
                                            <div style={{ width: "14px", display: "flex", justifyContent: "center" }}>
                                                {cart.order_financial_status === "paid" && (
                                                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#28a745" }} />
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="ui-table-service-tooltip-small_text">{t("No active seats")}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TableServiceToolTip;